import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2, Rocket } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public "Request access / waitlist" form for investors, wholesalers, and
 * agents who want to try FlipPilot. This is DIFFERENT from the seller-side
 * Contact form on /contact (that one collects home-sale leads).
 *
 * Feeds `/api/access-requests` and pings admin Telegram so approvals happen
 * within minutes of a submission.
 */
export default function RequestAccess() {
  const navigate = useNavigate();
  useSEO({
    title: "Request Access | FlipPilot",
    description:
      "Apply for early access to the FlipPilot acquisition platform — AI-powered lead generation, dialer, and offer automation for real-estate investors.",
    path: "/request-access",
  });

  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    role: "wholesaler", market: "", monthly_volume: "", why: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/access-requests`, form);
      setDone(true);
      toast.success("Request submitted — we'll be in touch shortly");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="py-24" data-testid="request-access-thanks">
        <div className="mkt-container max-w-lg mx-auto">
          <div className="mkt-card p-10 text-center">
            <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-5"
                 style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}>
              <CheckCircle2 size={26} strokeWidth={2.2} />
            </div>
            <h1 className="text-2xl font-bold">You&apos;re on the list.</h1>
            <p className="mt-3 text-sm text-[var(--mkt-text-muted)] leading-relaxed">
              We review new applications within one business day. If you&apos;re a
              good fit for the pilot, we&apos;ll email your login instructions
              directly from <span className="font-mono">team@flippilot.us</span>.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/")}
                data-testid="thanks-home-btn"
                className="mkt-btn mkt-btn-primary justify-center"
              >
                Back to home
              </button>
              <Link to="/partner"
                    data-testid="thanks-partner-btn"
                    className="mkt-btn mkt-btn-secondary justify-center">
                Partner sign-in
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20" data-testid="request-access-page">
      <div className="mkt-container max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-11 h-11 rounded-full grid place-items-center mx-auto mb-4"
               style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}>
            <Rocket size={18} strokeWidth={2.2} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--mkt-text-muted)]">
            Waiting List
          </div>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold">
            Request access to FlipPilot
          </h1>
          <p className="mt-3 text-sm md:text-base text-[var(--mkt-text-muted)] max-w-xl mx-auto">
            The platform is currently in a closed pilot for active wholesalers,
            fix-and-flippers, and buy-and-hold investors. Tell us a bit about
            your business and we&apos;ll follow up with a trial login within one
            business day.
          </p>
        </div>

        <form onSubmit={submit} className="mkt-card p-6 md:p-8 space-y-5"
              data-testid="request-access-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full name *" testid="ra-name">
              <input required value={form.name} onChange={update("name")}
                     data-testid="ra-name-input"
                     className={inputCls} placeholder="Jane Doe" />
            </Field>
            <Field label="Company / brokerage" testid="ra-company">
              <input value={form.company} onChange={update("company")}
                     data-testid="ra-company-input"
                     className={inputCls} placeholder="Acme Investments LLC" />
            </Field>
            <Field label="Email *" testid="ra-email">
              <input required type="email" value={form.email} onChange={update("email")}
                     data-testid="ra-email-input"
                     className={inputCls} placeholder="you@company.com" />
            </Field>
            <Field label="Mobile" testid="ra-phone">
              <input value={form.phone} onChange={update("phone")}
                     data-testid="ra-phone-input"
                     className={inputCls} placeholder="(555) 123-4567" />
            </Field>
            <Field label="What best describes you?" testid="ra-role">
              <select value={form.role} onChange={update("role")}
                      data-testid="ra-role-select"
                      className={inputCls}>
                <option value="wholesaler">Wholesaler</option>
                <option value="flipper">Fix & Flip</option>
                <option value="buy_hold">Buy & Hold / Landlord</option>
                <option value="agent">Real-Estate Agent</option>
                <option value="team_lead">Team Lead / Manager</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Primary market" testid="ra-market">
              <input value={form.market} onChange={update("market")}
                     data-testid="ra-market-input"
                     className={inputCls} placeholder="e.g. Cape Coral FL, Tampa Bay" />
            </Field>
            <Field label="Typical monthly deal volume" testid="ra-volume">
              <select value={form.monthly_volume} onChange={update("monthly_volume")}
                      data-testid="ra-volume-select"
                      className={inputCls}>
                <option value="">Select…</option>
                <option value="0-1">Just getting started (0–1)</option>
                <option value="2-5">2–5 deals / month</option>
                <option value="6-15">6–15 deals / month</option>
                <option value="16+">16+ deals / month</option>
              </select>
            </Field>
            <Field label="How did you hear about us?" testid="ra-referrer">
              <input value={form.referrer} onChange={update("referrer")}
                     data-testid="ra-referrer-input"
                     className={inputCls} placeholder="Referral, YouTube, Google, etc." />
            </Field>
          </div>

          <Field label="Why do you want to try FlipPilot?" testid="ra-why">
            <textarea rows={4} value={form.why} onChange={update("why")}
                      data-testid="ra-why-textarea"
                      className={`${inputCls} min-h-[110px]`}
                      placeholder="What are you trying to solve? Which features excite you the most? (optional)" />
          </Field>

          <div className="pt-2 flex flex-col sm:flex-row items-center gap-3 justify-between">
            <p className="text-[11px] text-[var(--mkt-text-muted)] max-w-md text-center sm:text-left">
              We only use your info to review the application and send trial
              access. No spam — you can opt out any time.
            </p>
            <button
              type="submit"
              disabled={busy}
              data-testid="ra-submit-btn"
              className="mkt-btn mkt-btn-primary justify-center min-w-[180px]"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {busy ? "Submitting…" : "Request access"}
            </button>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-[var(--mkt-text-muted)]">
          Already approved?{" "}
          <Link to="/partner" data-testid="ra-partner-link"
                className="text-[var(--mkt-accent-dark)] font-semibold hover:underline">
            Sign in here
          </Link>
        </p>
      </div>
    </section>
  );
}

const inputCls =
  "w-full border border-[var(--mkt-line)] rounded-lg px-3 py-2.5 text-sm " +
  "focus:outline-none focus:border-[var(--mkt-accent)] transition-colors bg-white";

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-xs uppercase tracking-widest text-[var(--mkt-text-muted)] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
